import { Lock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

// Wraps a single page's <Route element>. App.js's top-level auth gate
// already handles "not logged in" and "pending access" globally, so this
// only needs to check ONE page-specific permission - kept separate from
// that gate so a super_admin-only page (Access Management) can be
// protected the same way as a regular page.
export const ProtectedRoute = ({ page, anyOf, superAdminOnly = false, anyUser = false, children }) => {
  const { user, hasPageAccess } = useAuth();
  // Aug 2026: "admin" is a new tier alongside super_admin that can also
  // access Access Management (user's explicit ask) - kept the existing
  // `superAdminOnly` prop name (single usage site) rather than renaming
  // it everywhere for what both roles are now allowed to see.
  // Aug 28 2026: `anyUser` - no specific page permission needed, just a
  // valid signed-in session (Inbound STO Receipt, user's explicit ask:
  // "Anyone logged into the app; to start with").
  // Jun 2026: `anyOf` - a page two different audiences reach through
  // different rights (NPD Requests: the NPD team raises, the tooling
  // approvers only decide), where each section inside the page gates itself.
  const allowed = superAdminOnly ? (user?.role === "super_admin" || user?.role === "admin")
    : anyUser ? !!user
    : anyOf ? anyOf.some((k) => hasPageAccess(k))
    : hasPageAccess(page);

  if (!allowed) {
    // Oct 26 2026: a shop-floor login has exactly one page, so a wrong URL
    // used to leave him stranded on a bare message with no nav to click -
    // hand him the way back to his register.
    const firstPage = user?.allowed_pages?.length === 1 && user.allowed_pages[0] === "consumption_register";
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4" data-testid="access-denied-page">
        <div className="text-center">
          <Lock size={32} weight="fill" className="text-[#98A2B3] mx-auto mb-3" />
          <h1 className="font-heading text-lg font-bold text-[#101828]">Access Denied</h1>
          <p className="text-sm text-[#667085] mt-1">You don't have permission to view this page.</p>
          {firstPage && (
            <Link
              to="/consumption-register"
              className="inline-block mt-4 px-4 py-2 rounded-full bg-[#0E7C86] hover:bg-[#0B6B74] text-white text-sm font-bold font-heading transition-colors duration-150"
              data-testid="access-denied-back-to-register"
            >
              Go to Consumption Register
            </Link>
          )}
        </div>
      </div>
    );
  }

  return children;
};
